<template>
  <div class="editor-text">
    <div class="editor-text-title">
      <span>Edit text</span>
      <IdmButton
        :btnType="'small outline'"
        :disabled="!node.edited"
        :text="'Clear changes'"
        @click="clearTextChanges"
        data-test="text-editor-clear-changes-button"
      ></IdmButton>
    </div>
    <IdmTextEditor
      v-if="fontsNamesLoaded"
      :placeholder="textObj"
      :pixelIdSection="'pixelArea'"
      :hidePreview="true"
      @get-popup-error="getPopupError"
      @generate-data="generateData"
      @get-uploaded-fonts="getUploadedFonts"
      :fontsNames
      :uploadedFonts="uploadedFonts"
      :googleFonts="googleFonts"
      :btnText="'Preview'"
      :key="count"
      data-test="text-editor-component"
    />
  </div>
</template>

<script>
import { useBuilderStore } from "@/stores/builderStore";
import _ from 'lodash';
import { TextFields } from '@/helper/consts';
import { fontsCache } from '@/helper/fontsCache';
import googleFonts from '@/json/googleFonts.json';
import FontName from 'fontname';

export default {
  name: 'textEditor',

  props: ['node', 'sceneId'],

  data() {
    return {
      textObj: {},
      uploadedFonts: [],
      googleFonts: [],
      count: 0,
      skipFirstGenerate: false,
      previewTimeoutID: null,
      previewTimeout: 2000,
      fontsNames:[],
      fontsNamesLoaded: false,
    };
  },

  watch: {
    node() {
      this.resetTextEditor();
    },
    'node.branding'(newKey, oldKey) {
      this.resetTextEditor();
    },
    'node.edited'(newKey, oldKey) {
      if (!newKey) {
        this.resetTextEditor();
      }
    },
  },

  computed: {
    builderStore() {
      return useBuilderStore();
    }
  },

  methods: {
    resetTextEditor() {
      this.setTextObjProperties();
      this.count++;
    },
    setPreviewTimeout(generatedData) {
      this.clearTimeOut();
      this.previewTimeoutID = setTimeout(() => {
        this.builderStore.updateNodeValue({ node: this.node, data: generatedData });
        this.$emit('preview');
      }, this.previewTimeout);
    },
    clearTimeOut() {
      if (this.previewTimeoutID != null) {
        clearTimeout(this.previewTimeoutID);
        this.previewTimeoutID = null;
      }
    },
    clearTextChanges() {
      const isHidden = this.node.is_hidden;
      const tags = this.node.tags;

      this.builderStore.undoNodeChanges();

      if (isHidden) {
        console.info('show node');
        this.builderStore.hideNode(this.node);
      }

      if (!_.isEqual(tags.sort(), this.node.tags.sort())) {
        this.builderStore.updateNodeTags({node: this.node, tags: tags,});
      }

      this.$emit('preview');
      this.resetTextEditor();
    },
    clearAllChanges() {
      this.builderStore.undoNodeChanges();
      this.$emit('preview');
      this.resetTextEditor();
    },
    setTextObjProperties() {
      this.clearTimeOut();
      this.textObj = {};

      const node = this.builderStore.scene.nodes.find(n => n.key === this.node.key) || this.node;

      _.set(this.textObj, 'key', node.key);

      _.forEach(node, (val, key) => {
        if (_.indexOf(TextFields, key) >= 0) {
          _.set(this.textObj, key, val);
        }
      });

      this.skipFirstGenerate = true;
    },
    getPopupError(data) {
      this.$emit('showErrorPopup', data);
    },
    generateData(data) {
      console.log("🚀 ~ generateData ~ data:", data);
      console.log('data');
      console.log(data);
      if (this.skipFirstGenerate) {
        this.skipFirstGenerate = false;
        return;
      }

      if(data?.val.length === 1 && data?.val?.[0]?.scale) {
        data.font_size = Math.round(data.font_size * data.val[0].scale);
        delete data.val[0].scale;
      }

      if (data.debounce) {
        this.setPreviewTimeout(data);
      } else {
        this.builderStore.updateNodeValue({ node: this.node, data });
        this.$emit('preview');
      }
    },
    getUploadedFonts(data) {
      fontsCache.updateSceneFonts(this.sceneId, data);
    },
    async getFontsNames() {
      const fontPaths = this.node.val
          .filter(item => item.font_path)
          .map(item => item.font_path);

      for (const path of fontPaths) {
        const fontUrl = /^https?:\/\//.test(path) ? path : import.meta.env.VITE_APP_CUSTOM_CONTENT_URL + '/' + path;
        try {
          const response = await fetch(fontUrl);
          if (!response.ok) throw new Error(`HTTP error ${response.status}`);

          const blob = await response.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const fontMeta = FontName.parse(arrayBuffer)[0];

          this.fontsNames.push({
            link: path,
            label: fontMeta.fontFamily
          });
        } catch (error) {
          console.error(`Font parse error ${path}:`, error);
          if (path.includes('_') && !path.includes('/')) {
            const fontName = path.split('_')[0];
            this.fontsNames.push({
              link: path,
              label: fontName
            });
          }
        }
      }
    },
  },
  async created() {
    this.googleFonts = googleFonts;
    await this.getFontsNames();
    this.fontsNamesLoaded = true;
    this.setTextObjProperties();
    this.uploadedFonts = fontsCache.getSceneFonts(this.sceneId);
  },
};
</script>

<style lang="scss">
.idm-btn.primary.generate-data-btn {
  margin-top: 12px;
  margin-left: auto;
}

.editor-text {
  display: flex;
  flex-direction: column;
  margin-top: 14px;

  .editor-text-title {
    margin-bottom: 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--dark-500);

    span {
      font-weight: 700;
    }
  }
}
</style>
